import { throttle } from "lodash";
import {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
  SetStateAction,
  Dispatch,
} from "react";
import { createPortal } from "react-dom";
import { SelectedItem } from "..";
import styles from "./styles.module.scss";

/**
 * space for detecting scrolling
 */
const DETECT_SCROLL_HEIGHT = 20;

/**
 * TODO: there is an element "Uploads +" bar, need to determine whether it should be scrolling along.
 * for now, hardcode the height
 */
const ADDITIONAL_DETECT_SCROLL_TOP_HEIGHT = 60;

/**
 * auto scroll step in 20 ms
 */
const SCROLL_STEP = 10;

export default function useDrag2Select(
  ref: HTMLDivElement | null,
  selected: SelectedItem[],
  selectableRefs: Record<
    string,
    {
      element: HTMLElement | null;
      value: SelectedItem;
    }
  >,
  setSelected: Dispatch<SetStateAction<SelectedItem[]>>,
  storageDrive: StorageDrive,
) {
  const [area, setArea] = useState<number[][]>();

  const [scrollTop0, setScrollTop0] = useState(0);
  const [scrollTop1, setScrollTop1] = useState(0);
  const mouseClientYRef = useRef(0);

  const isDraggingRef = useRef(false);
  const [isSelectedDragging, setIsSelectedDragging] = useState(false);

  const handleSetIsSelectedDragging = useCallback(
    (arg) => {
      if (storageDrive === "note-attachment") {
        // do not allow move by dragging
        return;
      }
      return setIsSelectedDragging(arg);
    },
    [storageDrive],
  );

  const [autoScroll, setAutoScroll] = useState(0);

  const needScroll = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) {
        return setAutoScroll(0);
      }

      if (ref) {
        const topY =
          ref.offsetTop +
          DETECT_SCROLL_HEIGHT +
          ADDITIONAL_DETECT_SCROLL_TOP_HEIGHT;
        // need to recalculate every time to exclude the bottom bar existence impact
        const bottomY = window.innerHeight - DETECT_SCROLL_HEIGHT;

        // in the page top scroll position and direction is up
        if (e.clientY <= topY && e.clientY <= mouseClientYRef.current) {
          mouseClientYRef.current = e.clientY;

          return setAutoScroll(-SCROLL_STEP);
        }

        // in the page bottom scroll position and direction is down
        if (e.clientY >= bottomY && e.clientY >= mouseClientYRef.current) {
          mouseClientYRef.current = e.clientY;

          return setAutoScroll(SCROLL_STEP);
        }
      }
      // update prev event clientY
      mouseClientYRef.current = e.clientY;
      return setAutoScroll(0);
    },
    [ref],
  );

  useEffect(() => {
    if (!ref) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.classList.contains("dropdown-item")) {
        return;
      }
      if (e.buttons > 1) {
        return;
      }
      window.addEventListener("mouseup", handleMouseUp);

      e.preventDefault();
      e.stopPropagation();

      isDraggingRef.current = true;

      mouseClientYRef.current = e.clientY;
      const scrollTop0 = ref?.scrollTop || 0;
      // add listener to record position change
      setScrollTop0(scrollTop0);
      setScrollTop1(scrollTop0);
      setArea(() => [
        [e.clientX, e.clientY],
        [e.clientX, e.clientY],
      ]);
    };

    // reset all variables to initial state
    const reset = () => {
      isDraggingRef.current = false;
      setArea(undefined);
      setScrollTop0(0);
      setScrollTop1(0);
      setAutoScroll(0);
      mouseClientYRef.current = 0;

      setIsSelectedDragging(false);
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      reset();
      window.removeEventListener("mouseup", handleMouseUp);
    };

    const throttledMouseMove = throttle((e: MouseEvent) => {
      if (isDraggingRef.current === false) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // scroll 1
      const scrollTop1 = ref?.scrollTop || 0;
      setScrollTop1(scrollTop1);

      setArea((s) => [
        [(s || [])[0][0], (s || [])[0][1]],
        [e.clientX, e.clientY],
      ]);
      setAutoScroll(0);
      needScroll(e);
    }, 50);
    // keep window to detect out of page behavior
    window.addEventListener("mousemove", throttledMouseMove);
    window.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("mousemove", throttledMouseMove);
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, [scrollTop0, needScroll, ref, isSelectedDragging]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const scroll = (step: number) => {
      if (timeout) {
        clearTimeout(timeout);
      }

      if (isDraggingRef.current && ref && autoScroll) {
        if (step < 0 && ref.scrollTop + step > 0) {
          ref.scrollTop += step;
          setScrollTop1(ref.scrollTop);
        } else if (step > 0 && ref.scrollHeight - ref.scrollTop > step) {
          ref.scrollTop += step;
          setScrollTop1(ref.scrollTop);
        }

        if (isDraggingRef.current && step === autoScroll) {
          timeout = setTimeout(() => {
            scroll(step);
          }, 20);
        }
      }
    };
    scroll(autoScroll);
    return () => {
      return timeout && clearTimeout(timeout);
    };
  }, [autoScroll, ref]);

  const rect = useMemo(() => {
    if (isSelectedDragging || !(area && area[0] && area[1])) {
      return;
    }
    let y0 = area[0][1];

    let y1 = area[1][1];

    y0 += scrollTop0 - scrollTop1;

    return {
      // area,
      x: Math.min(area[0][0], area[1][0]),
      y: Math.min(y0, y1),
      // keep 1px since even if X or Y axis remain the same, still need some visual indicator
      width: Math.abs(area[1][0] - area[0][0]) || 1,
      height: Math.abs(y1 - y0) || 1,
    };
  }, [area, isSelectedDragging, scrollTop0, scrollTop1]);

  useEffect(() => {
    if (isSelectedDragging || !rect) return;

    const { x, y, width, height } = rect;
    if (x !== undefined && y !== undefined && width && height) {
      const x1 = x + width;
      const y1 = y + height;
      setSelected(() => []);
      if (Object.keys(selectableRefs).length) {
        const res: any[] = [];
        Object.values(selectableRefs).forEach(({ element, value }) => {
          if (!element) {
            return;
          }

          const {
            x: elX,
            y: elY,
            width: elWidth,
            height: elHeight,
          } = element.getBoundingClientRect();

          const isXInRange = x <= elX + elWidth && x1 >= elX;
          const isYInRange = y <= elY + elHeight && y1 >= elY;

          if (isXInRange && isYInRange) {
            res.push(value);
          }
        });

        setSelected(() => res);
      }
    }
  }, [isSelectedDragging, rect, selectableRefs, setSelected]);

  const DragElement = useMemo(() => {
    if (!isSelectedDragging && rect) {
      const nextOffsetTop = ref?.offsetTop || 0;
      const nextY =
        rect.y === undefined
          ? 0
          : rect.y > nextOffsetTop
          ? rect.y
          : nextOffsetTop;

      const nextHeight =
        (rect.y || 0) > nextOffsetTop || (rect.height || 0) < nextOffsetTop
          ? rect.height
          : (rect.y || 0) + ((rect.height || 0) - nextOffsetTop);

      return createPortal(
        <div
          className={styles["drag-range"]}
          style={{
            left: rect.x,
            top: nextY,
            width: rect.width,
            height: nextHeight,
          }}
        />,
        document.body,
      );
    }
    if (isSelectedDragging) {
      return (
        selected.length > 0 &&
        createPortal(
          <div
            className={styles["drag-files"]}
            style={{
              left: area?.[1]?.[0],
              top: area?.[1]?.[1],
            }}
          >
            <div className={styles["drag-files__container"]}>
              <div className={styles["drag-files__filename"]}>
                {selected[0].name}
              </div>
              <div className={styles["drag-files__count"]}>
                {selected.length}
              </div>
            </div>
            {selected.length > 1 &&
              Array.from(
                { length: selected.length - 1 < 2 ? selected.length - 1 : 2 },
                (_, i) => (
                  <div
                    className={styles["drag-files__overlay"]}
                    key={i}
                    style={{ left: (i + 1) * 4, top: (i + 1) * 4 }}
                  />
                ),
              )}
          </div>,
          document.body,
        )
      );
    }
    return null;
  }, [area, isSelectedDragging, rect, ref?.offsetTop, selected]);

  return {
    DragElement,
    isSelectedDragging,
    setIsSelectedDragging: handleSetIsSelectedDragging,
  };
}
